'use client';
import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Copy, Check, Clock, AlertCircle, Wallet } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useWalletAuth } from '@/contexts/WalletAuthContext';

interface ChainConfig {
  id: string;
  chain_key: string;
  chain_name: string;
  chain_symbol: string;
  token_symbol: string;
  token_name: string;
  wallet_address: string;
  network_badge: string;
  chain_color: string;
  chain_icon: string;
  is_active: boolean;
  is_primary: boolean;
}

interface CryptoPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  packageName: string;
  packageId: string;
  amountUsd: number;
}

export default function CryptoPaymentModal({
  isOpen,
  onClose,
  packageName,
  packageId,
  amountUsd,
}: CryptoPaymentModalProps) {
  const { walletUser, supabaseUser } = useWalletAuth();
  const [chains, setChains] = useState<ChainConfig[]>([]);
  const [selectedChain, setSelectedChain] = useState<ChainConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [orderCreated, setOrderCreated] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1800); // 30 min

  const userId = supabaseUser?.id || walletUser?.id;

  useEffect(() => {
    if (!isOpen) return;
    const supabase = createClient();
    supabase
      .from('crypto_chain_config')
      .select('*')
      .eq('is_active', true)
      .order('sort_order')
      .then(({ data }) => {
        const activeChains = data || [];
        setChains(activeChains);
        const primary = activeChains.find(c => c.is_primary) || activeChains[0] || null;
        setSelectedChain(primary);
        setLoading(false);
      });
  }, [isOpen]);

  // Countdown timer
  useEffect(() => {
    if (!orderCreated) return;
    const interval = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(interval); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [orderCreated]);

  const formatTime = (s: number) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const handleSelectChain = (chain: ChainConfig) => {
    setSelectedChain(chain);
    setOrderCreated(false);
    setOrderId(null);
    setTimeLeft(1800);
  };

  const handleCreateOrder = async () => {
    if (!selectedChain || !userId) return;
    const supabase = createClient();
    const { data } = await supabase.from('crypto_payment_orders').insert({
      user_id: userId,
      package_id: packageId,
      chain_key: selectedChain.chain_key,
      chain_name: selectedChain.chain_name,
      token_symbol: selectedChain.token_symbol,
      wallet_address: selectedChain.wallet_address,
      amount_usd: amountUsd,
      qr_data: selectedChain.wallet_address,
      status: 'pending',
      expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
    }).select().single();
    if (data) {
      setOrderId(data.id);
      setOrderCreated(true);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0A1628] border border-[rgba(22,194,255,0.2)] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[rgba(22,194,255,0.1)]">
          <div>
            <h2 className="text-base font-sora font-bold text-[#E6F1FF]">Crypto Payment</h2>
            <p className="text-xs text-[#93A4B8] mt-0.5">{packageName} · <span className="text-[#00E5A8] font-semibold">${amountUsd}</span></p>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#93A4B8] hover:text-[#E6F1FF] transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#16C2FF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : chains.length === 0 ? (
            <div className="text-center py-10">
              <AlertCircle size={32} className="text-[#F59E0B] mx-auto mb-3" />
              <p className="text-sm text-[#93A4B8]">No payment chains configured yet.</p>
              <p className="text-xs text-[#4A5568] mt-1">Admin needs to activate payment chains.</p>
            </div>
          ) : (
            <>
              {/* Chain Selector */}
              <div>
                <p className="text-xs font-medium text-[#93A4B8] mb-2">Select Payment Network</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {chains.map(chain => (
                    <button
                      key={chain.id}
                      onClick={() => handleSelectChain(chain)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-medium transition-all ${
                        selectedChain?.id === chain.id
                          ? 'border-[rgba(22,194,255,0.4)] bg-[rgba(22,194,255,0.08)] text-[#E6F1FF]'
                          : 'border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] text-[#93A4B8] hover:border-[rgba(22,194,255,0.2)] hover:text-[#E6F1FF]'
                      }`}
                    >
                      <span className="text-lg">{chain.chain_icon}</span>
                      <span className="font-semibold">{chain.token_symbol}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-mono" style={{ background: `${chain.chain_color}25`, color: chain.chain_color }}>
                        {chain.network_badge}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Selected chain details */}
              {selectedChain && (
                <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.1)] rounded-xl p-4 space-y-4">
                  {/* Chain info */}
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{selectedChain.chain_icon}</span>
                    <div>
                      <p className="text-sm font-semibold text-[#E6F1FF]">{selectedChain.chain_name}</p>
                      <p className="text-xs" style={{ color: selectedChain.chain_color }}>
                        {selectedChain.network_badge} · Send {selectedChain.token_symbol}
                      </p>
                    </div>
                  </div>

                  {selectedChain.wallet_address ? (
                    <>
                      {/* QR Code */}
                      <div className="flex justify-center">
                        <div className="bg-white p-3 rounded-xl">
                          <QRCodeSVG
                            value={selectedChain.wallet_address}
                            size={180}
                            level="M"
                            includeMargin={false}
                          />
                        </div>
                      </div>

                      {/* Amount */}
                      <div className="bg-[rgba(0,229,168,0.05)] border border-[rgba(0,229,168,0.15)] rounded-lg p-3 text-center">
                        <p className="text-xs text-[#93A4B8] mb-0.5">Amount to Send</p>
                        <p className="text-xl font-sora font-bold text-[#00E5A8]">${amountUsd} USD</p>
                        <p className="text-xs text-[#93A4B8] mt-0.5">in {selectedChain.token_symbol} ({selectedChain.network_badge})</p>
                      </div>

                      {/* Wallet address */}
                      <div>
                        <p className="text-xs text-[#93A4B8] mb-1.5">Send to this address:</p>
                        <div className="flex items-center gap-2 bg-[rgba(255,255,255,0.03)] border border-[rgba(22,194,255,0.1)] rounded-lg px-3 py-2">
                          <p className="flex-1 text-xs font-mono text-[#E6F1FF] break-all">{selectedChain.wallet_address}</p>
                          <button
                            onClick={() => handleCopy(selectedChain.wallet_address)}
                            className="flex-shrink-0 p-1 text-[#93A4B8] hover:text-[#16C2FF] transition-colors"
                          >
                            {copied ? <Check size={14} className="text-[#00E5A8]" /> : <Copy size={14} />}
                          </button>
                        </div>
                      </div>

                      {/* Timer */}
                      {orderCreated && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-[#93A4B8] flex items-center gap-1">
                            <Clock size={12} /> Order expires in
                          </span>
                          <span className={`font-mono font-semibold ${timeLeft < 300 ? 'text-red-400' : 'text-[#F59E0B]'}`}>
                            {formatTime(timeLeft)}
                          </span>
                        </div>
                      )}

                      {/* Warning */}
                      <div className="bg-[rgba(245,158,11,0.05)] border border-[rgba(245,158,11,0.15)] rounded-lg p-3">
                        <p className="text-xs text-[#F59E0B] font-medium mb-1">⚠ Important</p>
                        <ul className="text-xs text-[#93A4B8] space-y-0.5 list-disc list-inside">
                          <li>Send only <strong className="text-[#E6F1FF]">{selectedChain.token_symbol}</strong> on <strong className="text-[#E6F1FF]">{selectedChain.network_badge}</strong> network</li>
                          <li>Sending wrong token or network will result in permanent loss</li>
                          <li>After sending, contact support with your TX hash</li>
                        </ul>
                      </div>

                      {/* Create order button */}
                      {!orderCreated ? (
                        <button
                          onClick={handleCreateOrder}
                          className="w-full py-2.5 bg-gradient-to-r from-[#16C2FF] to-[#0EA5E9] text-[#07111F] font-semibold rounded-xl text-sm hover:opacity-90 transition-opacity"
                        >
                          I'm Ready to Pay — Generate Order
                        </button>
                      ) : (
                        <div className="text-center space-y-2">
                          <div className="flex items-center justify-center gap-2 text-[#00E5A8]">
                            <Check size={16} />
                            <span className="text-sm font-semibold">Order Created</span>
                          </div>
                          <p className="text-xs text-[#93A4B8]">Order ID: <span className="font-mono text-[#16C2FF]">{orderId?.slice(0, 8)}...</span></p>
                          <p className="text-xs text-[#93A4B8]">Send payment and contact support to confirm your order.</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <AlertCircle size={24} className="text-[#F59E0B] mx-auto mb-2" />
                      <p className="text-xs text-[#93A4B8]">Wallet address not configured for this chain.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
